import React, { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Wrench } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", description: "", active: true };

export const ServicesAdmin = () => {
  const { t } = useLang();
  const cfg = useConfig();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const err = (e) => toast.error(e?.response?.data?.detail || "Error");

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("service_name_required")); return; }
    try {
      if (editing) await api.put(`/config/services/${editing.id}`, form);
      else await api.post("/config/services", form);
      toast.success(t("service_saved"));
      setOpen(false);
      await cfg.reload();
    } catch (e) { err(e); }
  };

  const del = async (s) => {
    if (!window.confirm(`${t("delete")} ${s.name}?`)) return;
    try {
      await api.delete(`/config/services/${s.id}`);
      toast.success(t("service_deleted"));
      await cfg.reload();
    } catch (e) { err(e); }
  };

  const services = cfg?.services || [];

  return (
    <div className="space-y-4" data-testid="services-admin">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{t("services_desc")}</p>
        <Button data-testid="add-service-button" className="h-9 rounded-full bg-orange-500 hover:bg-orange-600 gap-2"
          onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }}>
          <Plus className="w-4 h-4" /> {t("add_service")}
        </Button>
      </div>

      {services.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed" data-testid="no-services">
          <Wrench className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">{t("no_services")}</p>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden shadow-sm rounded-2xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">{t("service_name")}</th>
                <th className="px-4 py-3">{t("description")}</th>
                <th className="px-4 py-3">{t("status")}</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`service-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{s.description || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={s.active ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                      {t(s.active ? "active" : "inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(s); setForm({ ...s, description: s.description || "" }); setOpen(true); }}
                      data-testid={`edit-service-${s.id}`} className="p-1.5 text-slate-400 hover:text-slate-900"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(s)} data-testid={`delete-service-${s.id}`}
                      className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_service")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("service_name")}</Label>
              <Input data-testid="service-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("description")}</Label>
              <Input data-testid="service-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="service-active-checkbox" checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              {t("active")}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="save-service-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesAdmin;
